/**
 * Type definitions for timeline dependency visualization system
 */

export type BubbleType = 'left' | 'right';
export type DependencyType = 'PREDECESSOR' | 'SUCCESSOR';

/**
 * Position and bounds of a timeline item in DOM coordinates
 */
export interface ItemBounds {
  itemId: number;
  x: number; // Left edge relative to timeline container
  y: number; // Top edge relative to timeline container
  width: number;
  height: number;
  rowId: string;
  columnId: string;
}

/**
 * 2D point coordinates
 */
export interface Point {
  x: number;
  y: number;
}

/**
 * Position of a connection bubble on an item
 */
export interface BubblePosition extends Point {
  type: BubbleType;
  itemId: number;
}

/**
 * Dependency line data for rendering
 */
export interface DependencyLineData {
  id: string;
  sourceItemId: number;
  targetItemId: number;
  type: DependencyType;
  path: string; // SVG path string
}

/**
 * Drag state during dependency creation
 */
export interface DragState {
  isDragging: boolean;
  source: { itemId: number; type: BubbleType; position: Point } | null;
  position: Point | null;
  target: { itemId: number; type: BubbleType } | null;
}

/**
 * Item dependency from API
 */
export interface ItemDependency {
  dependencyType: DependencyType;
  dependencyItemId: number;
}

/**
 * Callback for creating a new dependency
 */
export type OnDependencyCreateCallback = (dependency: {
  sourceItemId: number;
  dependencyType: DependencyType;
  dependencyItemId: number;
}) => void | Promise<void>;

/**
 * Callback for deleting a dependency
 */
export type OnDependencyDeleteCallback = (dependency: {
  sourceItemId: number;
  dependencyItemId: number;
}) => void | Promise<void>;

/**
 * Constants for dependency visualization
 */
export const DEPENDENCY_CONSTANTS = {
  HORIZONTAL_PADDING: 10, // px to extend lines horizontally
  BUBBLE_RADIUS: 6, // px radius of connection bubbles
  BUBBLE_HOVER_RADIUS: 8, // px radius when hovering
  LINE_STROKE_WIDTH: 1.5, // px width of dependency lines
  LINE_HOVER_STROKE_WIDTH: 2, // px width when hovering
} as const;
